import { useState, useEffect } from 'react';
import api from '../api/axios';

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const pct = (n) => (n == null ? '—' : `${n}%`);
const iso = (d) => d.toISOString().slice(0, 10);

function Card({ label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <p className="text-2xl font-extrabold text-[#7C3AED]">{value}</p>
      <p className="text-xs font-semibold text-gray-400 uppercase mt-1">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function Table({ title, head, rows }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-4 border-b"><h3 className="font-bold text-[#0D1B3D]">{title}</h3></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50/60 text-gray-400 text-[10px] uppercase border-b border-gray-100">
            <tr>{head.map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={head.length} className="px-4 py-6 text-center text-gray-400">No data.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-gray-50">
                {r.map((c, j) => <td key={j} className="px-4 py-3">{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminBillingReports() {
  const [from, setFrom] = useState(iso(new Date(Date.now() - 29 * 86400000)));
  const [to, setTo] = useState(iso(new Date()));
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/billing-reports', { params: { from, to } });
      setD(res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-gray-400 uppercase">Period</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <span className="text-gray-400 text-sm">to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <button onClick={load} disabled={loading}
          className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
          {loading ? 'Loading…' : 'Apply'}
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {d && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card label="MRR" value={inr(d.subscriptions.mrr)} sub={`${inr(d.subscriptions.mrr_auto_renewing)} on auto-pay`} />
            <Card label="Paid employers" value={d.subscriptions.active_paid} />
            <Card label="Free employers" value={d.subscriptions.free_employers} />
            <Card label="New paid (period)" value={d.acquisition.new_paid_employers} />
            <Card label="Free → paid (signups in period)" value={pct(d.acquisition.cohort_conversion_rate)}
              sub={`${d.acquisition.signed_up_and_paid} of ${d.acquisition.signed_up} signups`} />
            <Card label="Free → paid (overall)" value={pct(d.acquisition.overall_conversion_rate)}
              sub={`${d.acquisition.ever_paid} of ${d.subscriptions.total_employers} employers`} />
            <Card label="Revenue (period)" value={inr(d.revenue.total)} />
            <Card label="Renewals" value={d.renewals.count} sub={inr(d.renewals.revenue)} />
            <Card label="Cancellations" value={d.churn.cancellations} sub="Auto-pay switched off" />
            <Card label="Churned employers" value={d.churn.churned_employers} sub={`Churn rate ${pct(d.churn.churn_rate)}`} />
            <Card label="Top-selling plan" value={d.revenue.top_plan?.plan_name || '—'}
              sub={d.revenue.top_plan ? `${d.revenue.top_plan.new_sales} sales · ${inr(d.revenue.top_plan.revenue)}` : ''} />
            <Card label="Featured jobs" value={d.featured.live_now} sub={`${d.featured.jobs_featured_in_period} featured in period`} />
          </div>

          <Table title="Active subscriptions by plan"
            head={['Plan', 'Subscribers', 'MRR', 'Auto-renewing']}
            rows={d.subscriptions.by_plan.map((p) => [p.plan_name, p.subscribers, inr(p.mrr), p.auto_renewing])} />

          <Table title="Revenue by plan"
            head={['Plan', 'New sales', 'Renewals', 'Revenue']}
            rows={d.revenue.by_plan.map((p) => [p.plan_name, p.new_sales, p.renewals, inr(p.revenue)])} />

          <Table title="Revenue by add-on (Credits, Boost, Featured)"
            head={['Add-on', 'Purchases', 'Revenue']}
            rows={d.revenue.addons.map((a) => [a.label, a.payments, inr(a.revenue)])} />

          <Table title="Other products"
            head={['Product', 'Purchases', 'Revenue']}
            rows={d.revenue.other_products.map((a) => [a.label, a.payments, inr(a.revenue)])} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card label="Credits added" value={d.credits.added} />
            <Card label="Credits used" value={d.credits.used} />
            <Card label="Unlocks" value={d.credits.unlocks} />
            <Card label="Credits outstanding" value={d.credits.outstanding} />
          </div>

          <Table title="Top credit users"
            head={['Employer', 'Email', 'Credits used']}
            rows={d.credits.top_users.map((u) => [u.name, u.email, u.used])} />
        </>
      )}
    </div>
  );
}